/**
 * NutrientDeficiencyCalculator.tsx
 * Calculator for diagnosing nutrient deficiencies based on element concentrations
 */

import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Card, 
  CardHeader, 
  CardContent, 
  Typography, 
  TextField,
  Button,
  Grid as MuiGrid, 
  MenuItem,
  Paper,
  FormControl,
  InputLabel,
  Select,
  SelectChangeEvent,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  ListItem,
  List,
  ListItemText,
  ListItemIcon,
  Chip,
} from '@mui/material';

// Custom Grid component to handle different prop patterns
const Grid = ({ children, size, xs, md, ...props }: any) => {
  if (size) {
    return (
      <MuiGrid size={size} {...props}>
        {children}
      </MuiGrid>
    );
  }
  return (
    <MuiGrid size={{ xs, md }} {...props}>
      {children}
    </MuiGrid>
  );
};
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import axios from 'axios';
import {
  UnifiedHydroBuddyEngine,
  createUnifiedHydroBuddyEngine,
  UnifiedSalt,
  UnifiedWaterQuality,
  UnifiedTargetFormulation,
  UnifiedCalculationOptions
} from '../../utils/UnifiedHydroBuddyEngine';
import { Element } from '../../types/Element';
import { Salt } from '../../types/Salt';

// Common elements in hydroponics with their optimal ranges
const ELEMENT_RANGES = {
  'N (NO3-)': { min: 150, low: 100, high: 250, max: 300, unit: 'ppm' },
  'N (NH4+)': { min: 0, low: 0, high: 25, max: 40, unit: 'ppm' },
  'P': { min: 30, low: 20, high: 50, max: 80, unit: 'ppm' },
  'K': { min: 150, low: 100, high: 300, max: 400, unit: 'ppm' },
  'Ca': { min: 150, low: 100, high: 200, max: 250, unit: 'ppm' },
  'Mg': { min: 40, low: 25, high: 75, max: 100, unit: 'ppm' },
  'S': { min: 50, low: 30, high: 120, max: 200, unit: 'ppm' },
  'Fe': { min: 2, low: 1, high: 5, max: 7, unit: 'ppm' },
  'Mn': { min: 0.5, low: 0.3, high: 1, max: 3, unit: 'ppm' },
  'Zn': { min: 0.3, low: 0.2, high: 0.6, max: 1.0, unit: 'ppm' },
  'B': { min: 0.3, low: 0.2, high: 0.5, max: 1.0, unit: 'ppm' },
  'Cu': { min: 0.05, low: 0.03, high: 0.1, max: 0.5, unit: 'ppm' },
  'Mo': { min: 0.05, low: 0.03, high: 0.1, max: 0.5, unit: 'ppm' },
};

// Element categories for visual organization
const ELEMENT_CATEGORIES = {
  'Macronutrients': ['N (NO3-)', 'N (NH4+)', 'P', 'K', 'Ca', 'Mg', 'S'],
  'Micronutrients': ['Fe', 'Mn', 'Zn', 'B', 'Cu', 'Mo']
};

interface DeficiencyDiagnosis {
  element: string;
  value: number;
  status: 'deficient' | 'low' | 'optimal' | 'high' | 'excess';
  message: string;
  symptoms: string[];
  treatment: string;
}

interface NutrientRatio {
  name: string;
  ratio: string;
  status: 'optimal' | 'imbalanced';
  optimal: string;
}

interface FormData {
  elementLevels: Record<string, number>;
  concentrationUnit: 'ppm' | 'mg/L' | 'mmol/L' | 'meq/L';
  cropType: string;
  growthStage: string;
}

const initialFormData: FormData = {
  elementLevels: Object.keys(ELEMENT_RANGES).reduce((acc, element) => {
    acc[element] = 0;
    return acc;
  }, {} as Record<string, number>),
  concentrationUnit: 'ppm',
  cropType: 'general',
  growthStage: 'vegetative'
};

const NutrientDeficiencyCalculator: React.FC = () => {
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [diagnoses, setDiagnoses] = useState<DeficiencyDiagnosis[]>([]);
  const [ratios, setRatios] = useState<NutrientRatio[]>([]);
  const [elements, setElements] = useState<Element[]>([]);
  const [salts, setSalts] = useState<Salt[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch element data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [elementsResponse, saltsResponse] = await Promise.all([
          axios.get('http://localhost:3001/api/elements'),
          axios.get('http://localhost:3001/api/salts')
        ]);
        
        setElements(elementsResponse.data);
        setSalts(saltsResponse.data);
      } catch (error) {
        console.error('Error fetching data:', error);
        setError('Failed to load elements data');
      }
    };

    fetchData();
  }, []);

  // Helper to get element name from symbol
  const getElementName = (symbol: string): string => {
    const element = elements.find(el => el.symbol === symbol);
    return element ? element.name : symbol;
  };

  // Handle input changes for element concentrations
  const handleElementChange = (element: string, value: number) => {
    setFormData({
      ...formData,
      elementLevels: {
        ...formData.elementLevels,
        [element]: value
      }
    });
  };

  // Handle dropdown changes
  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Diagnose nutrient deficiencies
  const handleDiagnose = () => {
    setLoading(true);
    setError(null);

    try {
      // Simple ratio calculations without engine dependency
      // const engine = createUnifiedHydroBuddyEngine();
      
      // Create diagnoses
      const newDiagnoses: DeficiencyDiagnosis[] = [];
      
      // Check each element concentration against optimal ranges
      Object.entries(formData.elementLevels).forEach(([element, value]) => {
        if (value === 0) return; // Skip elements with no data
        
        const range = ELEMENT_RANGES[element as keyof typeof ELEMENT_RANGES];
        if (!range) return;
        
        let status: DeficiencyDiagnosis['status'];
        let message = '';
        let symptoms: string[] = [];
        let treatment = '';
        
        if (value < range.min) {
          status = 'deficient';
          message = `${getElementName(element)} is deficient at ${value} ${range.unit} (recommended minimum: ${range.min} ${range.unit})`;
          symptoms = getNutrientDeficiencySymptoms(element);
          treatment = getDeficiencyTreatment(element);
        } else if (value < range.low) {
          status = 'low';
          message = `${getElementName(element)} is low at ${value} ${range.unit} (optimal range: ${range.low}-${range.high} ${range.unit})`;
          symptoms = getNutrientDeficiencySymptoms(element);
          treatment = getDeficiencyTreatment(element);
        } else if (value > range.max) {
          status = 'excess';
          message = `${getElementName(element)} is excessive at ${value} ${range.unit} (recommended maximum: ${range.max} ${range.unit})`;
          symptoms = getNutrientExcessSymptoms(element);
          treatment = getExcessTreatment(element);
        } else if (value > range.high) {
          status = 'high';
          message = `${getElementName(element)} is high at ${value} ${range.unit} (optimal range: ${range.low}-${range.high} ${range.unit})`;
          symptoms = getNutrientExcessSymptoms(element);
          treatment = getExcessTreatment(element);
        } else {
          status = 'optimal';
          message = `${getElementName(element)} is optimal at ${value} ${range.unit}`;
          symptoms = [];
          treatment = 'No treatment needed.';
        }
        
        newDiagnoses.push({
          element,
          value,
          status,
          message,
          symptoms,
          treatment
        });
      });
      
      setDiagnoses(newDiagnoses);
      
      // Calculate nutrient ratios
      const newRatios: NutrientRatio[] = [];
      
      // N:P:K ratio
      const nValue = (formData.elementLevels['N (NO3-)'] || 0) + (formData.elementLevels['N (NH4+)'] || 0);
      const pValue = formData.elementLevels['P'] || 0;
      const kValue = formData.elementLevels['K'] || 0;
      
      if (nValue > 0 && pValue > 0 && kValue > 0) {
        // Calculate simple N:P:K ratio
        const nRatio = Math.round(nValue / Math.min(nValue, pValue, kValue));
        const pRatio = Math.round(pValue / Math.min(nValue, pValue, kValue));
        const kRatio = Math.round(kValue / Math.min(nValue, pValue, kValue));
        const npkRatio = `${nRatio}:${pRatio}:${kRatio}`;

        newRatios.push({
          name: 'N:P:K',
          ratio: npkRatio,
          status: npkRatio === '1:1:1' ? 'optimal' : 'imbalanced',
          optimal: '1:1:1 to 1:2:3 (varies by crop)'
        });
      }

      // Ca:Mg ratio
      const caValue = formData.elementLevels['Ca'] || 0;
      const mgValue = formData.elementLevels['Mg'] || 0;

      if (caValue > 0 && mgValue > 0) {
        const ratio = caValue / mgValue;
        const caMgRatio = `${Math.round(ratio * 10) / 10}:1`;
        const optimalCaMg = '3:1 to 5:1';
        const isCaMgOptimal = ratio >= 3 && ratio <= 5;

        newRatios.push({
          name: 'Ca:Mg',
          ratio: caMgRatio,
          status: isCaMgOptimal ? 'optimal' : 'imbalanced',
          optimal: optimalCaMg
        });
      }

      // NO3:NH4 ratio
      const no3Value = formData.elementLevels['N (NO3-)'] || 0;
      const nh4Value = formData.elementLevels['N (NH4+)'] || 0;

      if (no3Value > 0) {
        const ratio = nh4Value > 0 ? no3Value / nh4Value : no3Value;
        const no3Nh4Ratio = nh4Value > 0 ? `${Math.round(ratio * 10) / 10}:1` : `${no3Value}:0`;
        const optimalNo3Nh4 = '4:1 to 10:1';
        const isNo3Nh4Optimal = no3Value > nh4Value * 4;

        newRatios.push({
          name: 'NO₃⁻:NH₄⁺',
          ratio: no3Nh4Ratio,
          status: isNo3Nh4Optimal ? 'optimal' : 'imbalanced',
          optimal: optimalNo3Nh4
        });
      }
      
      setRatios(newRatios);
      
    } catch (error) {
      console.error('Error diagnosing deficiencies:', error);
      setError('Failed to diagnose nutrient deficiencies');
    } finally {
      setLoading(false);
    }
  };
  
  // Helper functions for symptoms and treatments
  const getNutrientDeficiencySymptoms = (element: string): string[] => {
    switch(element) {
      case 'N (NO3-)':
      case 'N (NH4+)':
        return [
          'Yellowing of older leaves (chlorosis)',
          'Stunted growth',
          'Smaller leaves',
          'Premature leaf drop',
          'Purple discoloration on stems and leaf undersides'
        ];
      case 'P':
        return [
          'Dark green leaves with purple/red tint',
          'Stunted growth',
          'Poor root development',
          'Delayed maturity',
          'Poor fruit or seed development'
        ];
      case 'K':
        return [
          'Yellowing between leaf veins',
          'Scorching or browning of leaf margins',
          'Weak stems',
          'Increased susceptibility to diseases',
          'Poor fruit quality'
        ];
      case 'Ca':
        return [
          'Curled or hooked leaf tips',
          'Blossom end rot in fruits',
          'Death of growing points',
          'Leaf tip burn',
          'Poor root development'
        ];
      case 'Mg':
        return [
          'Interveinal chlorosis in older leaves',
          'Leaf margins turning yellow then brown',
          'Leaves become brittle',
          'Premature leaf drop'
        ];
      case 'S':
        return [
          'Yellowing of younger leaves',
          'Small, spindly plants',
          'Delayed maturity'
        ];
      case 'Fe':
        return [
          'Interveinal chlorosis in young leaves',
          'Stunted growth',
          'Pale, yellow leaves with green veins'
        ];
      case 'B':
        return [
          'Death of growing points',
          'Brittle, misshapen leaves',
          'Hollow stems or fruit',
          'Poor pollination'
        ];
      case 'Mn':
        return [
          'Interveinal chlorosis with small green spots',
          'Grey spots on leaves',
          'Poor seed germination'
        ];
      case 'Zn':
        return [
          'Interveinal chlorosis',
          'Stunted growth',
          'Small leaves',
          '"Little leaf" condition'
        ];
      case 'Cu':
        return [
          'Twisted or malformed leaves',
          'Pale leaves',
          'Stunted growth',
          'Dieback of shoots'
        ];
      case 'Mo':
        return [
          'Mottling and marginal chlorosis',
          'Leaf malformation',
          'Stunted growth'
        ];
      default:
        return ['No specific symptoms data available'];
    }
  };
  
  const getNutrientExcessSymptoms = (element: string): string[] => {
    switch(element) {
      case 'N (NO3-)':
      case 'N (NH4+)':
        return [
          'Excessive vegetative growth',
          'Dark green foliage',
          'Reduced flowering and fruiting',
          'Delayed maturity',
          'Increased susceptibility to diseases and pests'
        ];
      case 'P':
        return [
          'Induced deficiencies of Zn, Fe, and Cu',
          'Stunted growth',
          'Dark, dull green leaves'
        ];
      case 'K':
        return [
          'Induced Ca and Mg deficiencies',
          'Leaf scorching',
          'Salt burn symptoms'
        ];
      case 'Ca':
        return [
          'Reduced availability of other nutrients',
          'High pH issues',
          'Chlorosis symptoms'
        ];
      case 'Mg':
        return [
          'Antagonism with Ca and K uptake',
          'Salt stress symptoms'
        ];
      case 'Fe':
        return [
          'Bronze or black spotting on leaves',
          'Reduced growth',
          'Dark discoloration of foliage'
        ];
      case 'B':
        return [
          'Leaf tip and margin burn',
          'Leaf yellowing',
          'Death of leaf tissue',
          'Plants appear wilted even with adequate water'
        ];
      case 'Mn':
        return [
          'Brown spots on leaves',
          'Crinkled leaves',
          'Induced Fe deficiency'
        ];
      case 'Zn':
        return [
          'Induced Fe deficiency',
          'Stunted growth'
        ];
      case 'Cu':
        return [
          'Reduced root growth',
          'Dark, stunted root systems',
          'Iron chlorosis symptoms'
        ];
      default:
        return ['No specific excess symptoms data available'];
    }
  };
  
  const getDeficiencyTreatment = (element: string): string => {
    switch(element) {
      case 'N (NO3-)':
      case 'N (NH4+)':
        return 'Add nitrate or ammonium based fertilizers. For quick correction, use calcium nitrate or potassium nitrate.';
      case 'P':
        return 'Add phosphate fertilizers such as monopotassium phosphate or triple superphosphate.';
      case 'K':
        return 'Add potassium-containing fertilizers such as potassium sulfate, potassium nitrate or monopotassium phosphate.';
      case 'Ca':
        return 'Add calcium nitrate or calcium chloride. Ensure pH is not too low.';
      case 'Mg':
        return 'Add magnesium sulfate (Epsom salt) or magnesium nitrate.';
      case 'S':
        return 'Add sulfate-containing fertilizers like potassium sulfate or magnesium sulfate.';
      case 'Fe':
        return 'Add iron chelates (Fe-DTPA, Fe-EDDHA) depending on your pH level.';
      case 'B':
        return 'Add borax or boric acid in very small quantities.';
      case 'Mn':
        return 'Add manganese sulfate or manganese chelate.';
      case 'Zn':
        return 'Add zinc sulfate or zinc chelate.';
      case 'Cu':
        return 'Add copper sulfate or copper chelate in very small quantities.';
      case 'Mo':
        return 'Add sodium molybdate in extremely small quantities.';
      default:
        return 'No specific treatment data available.';
    }
  };
  
  const getExcessTreatment = (element: string): string => {
    switch(element) {
      case 'N (NO3-)':
      case 'N (NH4+)':
        return 'Reduce nitrogen fertilization. Flush growing medium with clean water.';
      case 'P':
        return 'Reduce phosphate fertilization. Consider adding iron and zinc supplements.';
      case 'K':
        return 'Reduce potassium fertilization. Balance with calcium and magnesium supplements.';
      case 'Ca':
        return 'Check pH and adjust if too high. Reduce calcium-containing fertilizers.';
      case 'Mg':
        return 'Reduce magnesium fertilization. Increase potassium to balance.';
      case 'Fe':
      case 'Mn':
      case 'Zn':
      case 'Cu':
        return 'Flush growing medium with clean water. Ensure pH is in proper range. Temporarily stop adding micronutrient supplements.';
      case 'B':
        return 'Immediately flush growing medium with clean water. Boron excess can be difficult to correct and potentially toxic.';
      default:
        return 'Flush growing medium with clean water. Rebalance nutrient solution with lower levels.';
    }
  };

  return (
    <Card sx={{ mb: 4 }}>
      <CardHeader 
        title="Nutrient Deficiency Calculator" 
        subheader="Diagnose and correct nutrient imbalances in your hydroponic system" 
      />
      <CardContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}
        
        <Grid size={{ container: true, spacing: 3 }}>          {/* General Settings */}
          <Grid size={{ xs: 12 }}>
            <Paper elevation={1} sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>General Settings</Typography>
              <Grid size={{ container: true, spacing: 2 }}>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Concentration Unit</InputLabel>
                    <Select
                      name="concentrationUnit"
                      value={formData.concentrationUnit}
                      onChange={handleSelectChange}
                      label="Concentration Unit"
                    >
                      <MenuItem value="ppm">ppm</MenuItem>
                      <MenuItem value="mg/L">mg/L</MenuItem>
                      <MenuItem value="mmol/L">mmol/L</MenuItem>
                      <MenuItem value="meq/L">meq/L</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Crop Type</InputLabel>
                    <Select
                      name="cropType"
                      value={formData.cropType}
                      onChange={handleSelectChange}
                      label="Crop Type"
                    >
                      <MenuItem value="general">General Hydroponics</MenuItem>
                      <MenuItem value="leafy">Leafy Greens</MenuItem>
                      <MenuItem value="fruiting">Fruiting Plants</MenuItem>
                      <MenuItem value="herbs">Herbs</MenuItem>
                      <MenuItem value="strawberry">Strawberry</MenuItem>
                      <MenuItem value="tomato">Tomato</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <FormControl fullWidth margin="normal">
                    <InputLabel>Growth Stage</InputLabel>
                    <Select
                      name="growthStage"
                      value={formData.growthStage}
                      onChange={handleSelectChange}
                      label="Growth Stage"
                    >
                      <MenuItem value="seedling">Seedling</MenuItem>
                      <MenuItem value="vegetative">Vegetative</MenuItem>
                      <MenuItem value="flowering">Flowering/Fruiting</MenuItem>
                      <MenuItem value="ripening">Ripening</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>
          </Grid>          {/* Nutrient Input Panels */}          {Object.entries(ELEMENT_CATEGORIES).map(([category, elements]) => (
            <Grid size={{ xs: 12, md: 6 }} key={category}>
              <Paper elevation={1} sx={{ p: 2, mb: 2, height: '100%' }}>
                <Typography variant="h6" gutterBottom>{category}</Typography>
                <Grid size={{ container: true, spacing: 2 }}>
                  {elements.map(element => (
                    <Grid size={{ xs: 6 }} key={element}>
                      <TextField
                        fullWidth
                        label={`${getElementName(element)} (${element})`}
                        type="number"
                        value={formData.elementLevels[element] || 0}
                        onChange={(e) => handleElementChange(element, Number(e.target.value))}
                        InputProps={{
                          endAdornment: <Typography variant="caption">{formData.concentrationUnit}</Typography>
                        }}
                        inputProps={{ min: 0, step: element.match(/Fe|Mn|Zn|B|Cu|Mo/) ? 0.1 : 1 }}
                        margin="normal"
                        size="small"
                      />
                    </Grid>
                  ))}
                </Grid>
              </Paper>
            </Grid>
          ))}
            {/* Calculate Button */}
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button 
                variant="contained" 
                color="primary" 
                onClick={handleDiagnose}
                disabled={loading}
                sx={{ minWidth: 150 }}
              >
                {loading ? 'Diagnosing...' : 'Diagnose Deficiencies'}
              </Button>
            </Box>
          </Grid>
            {/* Results Section */}
          {diagnoses.length > 0 && (
            <Grid size={{ xs: 12 }}>
              <Paper elevation={1} sx={{ p: 2, mt: 2 }}>
                <Typography variant="h6" gutterBottom>Nutrient Status</Typography>
                
                {/* Nutrient Ratios */}
                {ratios.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1">Key Nutrient Ratios</Typography>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Ratio</TableCell>
                            <TableCell>Current Value</TableCell>
                            <TableCell>Optimal Range</TableCell>
                            <TableCell>Status</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {ratios.map((ratio) => (
                            <TableRow key={ratio.name}>
                              <TableCell>{ratio.name}</TableCell>
                              <TableCell>{ratio.ratio}</TableCell>
                              <TableCell>{ratio.optimal}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={ratio.status} 
                                  color={ratio.status === 'optimal' ? 'success' : 'warning'}
                                  size="small"
                                />
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Box>
                )}
                
                {/* Nutrient Status */}
                <Box>
                  <Typography variant="subtitle1">Element Status</Typography>
                  <List>
                    {diagnoses
                      .filter(d => d.status !== 'optimal')
                      .map((diagnosis) => (
                        <ListItem key={diagnosis.element} alignItems="flex-start" divider>
                          <ListItemIcon>
                            {diagnosis.status === 'deficient' ? (
                              <ErrorOutlineIcon color="error" />
                            ) : diagnosis.status === 'low' ? (
                              <TrendingDownIcon color="warning" />
                            ) : diagnosis.status === 'excess' ? (
                              <ErrorOutlineIcon color="error" />
                            ) : (
                              <TrendingUpIcon color="warning" />
                            )}
                          </ListItemIcon>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2">
                                {getElementName(diagnosis.element)} ({diagnosis.element})
                                <Chip 
                                  label={diagnosis.status} 
                                  color={
                                    diagnosis.status === 'optimal' ? 'success' :
                                    diagnosis.status === 'deficient' || diagnosis.status === 'excess' ? 'error' : 'warning'
                                  }
                                  size="small"
                                  sx={{ ml: 1 }}
                                />
                              </Typography>
                            }
                            secondary={
                              <>
                                <Typography variant="body2" component="span">{diagnosis.message}</Typography>
                                <Typography variant="subtitle2" sx={{ mt: 1 }}>Symptoms:</Typography>
                                <ul style={{ margin: '4px 0' }}>
                                  {diagnosis.symptoms.map((symptom, i) => (
                                    <li key={i}>{symptom}</li>
                                  ))}
                                </ul>
                                <Typography variant="subtitle2" sx={{ mt: 1 }}>Treatment:</Typography>
                                <Typography variant="body2">{diagnosis.treatment}</Typography>
                              </>
                            }
                          />
                        </ListItem>
                    ))}
                    
                    {diagnoses
                      .filter(d => d.status === 'optimal')
                      .length > 0 && (
                        <ListItem>
                          <ListItemIcon>
                            <CheckCircleOutlineIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary={`${diagnoses.filter(d => d.status === 'optimal').length} elements at optimal levels`}
                          />
                        </ListItem>
                    )}
                  </List>
                </Box>
              </Paper>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default NutrientDeficiencyCalculator;
